import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Call, Device } from '@twilio/voice-sdk';
import { api } from './api';

export type SoftphoneStatus = 'offline' | 'ready' | 'connecting' | 'incoming' | 'oncall';

type SoftphoneContextValue = {
  status: SoftphoneStatus;
  muted: boolean;
  incomingFrom: string | null;
  activeNumber: string | null;
  makeCall: (to: string) => void;
  acceptCall: () => void;
  rejectCall: () => void;
  hangUp: () => void;
  toggleMute: () => void;
};

const SoftphoneContext = createContext<SoftphoneContextValue | null>(null);

async function fetchToken(): Promise<string> {
  const { data } = await api.get<{ token: string }>('/v1/telephony/token');
  return data.token;
}

/**
 * Owns the Twilio Voice Device for the admin browser: registers to receive
 * inbound calls, places outbound calls, and exposes call controls. Degrades to
 * 'offline' (no crash) if Twilio isn't configured or the mic is blocked.
 */
export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const deviceRef = useRef<Device | null>(null);
  const activeRef = useRef<Call | null>(null);
  const incomingRef = useRef<Call | null>(null);

  const [status, setStatus] = useState<SoftphoneStatus>('offline');
  const [muted, setMuted] = useState(false);
  const [incomingFrom, setIncomingFrom] = useState<string | null>(null);
  const [activeNumber, setActiveNumber] = useState<string | null>(null);

  function resetToReady() {
    activeRef.current = null;
    incomingRef.current = null;
    setIncomingFrom(null);
    setActiveNumber(null);
    setMuted(false);
    setStatus(deviceRef.current ? 'ready' : 'offline');
  }

  function wireActive(call: Call) {
    call.on('accept', () => setStatus('oncall'));
    call.on('disconnect', resetToReady);
    call.on('cancel', resetToReady);
    call.on('error', resetToReady);
  }

  useEffect(() => {
    let cancelled = false;
    let device: Device | null = null;

    (async () => {
      try {
        const token = await fetchToken();
        if (cancelled) return;

        device = new Device(token, { logLevel: 'error' });
        deviceRef.current = device;

        device.on('registered', () => setStatus('ready'));
        device.on('unregistered', () => setStatus('offline'));
        device.on('error', (e: { message?: string }) => {
          console.error('[softphone]', e?.message ?? e);
        });
        device.on('tokenWillExpire', async () => {
          try {
            device?.updateToken(await fetchToken());
          } catch {
            /* keep the current token; it may still have life left */
          }
        });
        device.on('incoming', (call: Call) => {
          incomingRef.current = call;
          setIncomingFrom(call.parameters.From ?? null);
          setStatus('incoming');
          call.on('cancel', resetToReady);
          call.on('disconnect', resetToReady);
          call.on('reject', resetToReady);
        });

        await device.register();
      } catch (e) {
        console.error('[softphone] init failed', e);
        if (!cancelled) setStatus('offline');
      }
    })();

    return () => {
      cancelled = true;
      device?.destroy();
      deviceRef.current = null;
    };
  }, []);

  function makeCall(to: string) {
    const device = deviceRef.current;
    if (!device || !to) return;
    setActiveNumber(to);
    setStatus('connecting');
    device
      .connect({ params: { To: to } })
      .then((call) => {
        activeRef.current = call;
        wireActive(call);
      })
      .catch(() => resetToReady());
  }

  function acceptCall() {
    const call = incomingRef.current;
    if (!call) return;
    activeRef.current = call;
    setActiveNumber(incomingFrom);
    wireActive(call);
    call.accept();
    incomingRef.current = null;
    setStatus('oncall');
  }

  function rejectCall() {
    incomingRef.current?.reject();
    resetToReady();
  }

  function hangUp() {
    activeRef.current?.disconnect();
  }

  function toggleMute() {
    const call = activeRef.current;
    if (!call) return;
    const next = !muted;
    call.mute(next);
    setMuted(next);
  }

  const value: SoftphoneContextValue = {
    status,
    muted,
    incomingFrom,
    activeNumber,
    makeCall,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
  };

  return <SoftphoneContext.Provider value={value}>{children}</SoftphoneContext.Provider>;
}

export function useSoftphone(): SoftphoneContextValue {
  const ctx = useContext(SoftphoneContext);
  if (!ctx) {
    throw new Error('useSoftphone must be used within a SoftphoneProvider');
  }
  return ctx;
}
